import React, { useState, useEffect, useRef } from "react"
import Icon from "../AppIcon"
import Button from "../ui/Button"
import {
  subscribeMentorshipMessages,
  sendMentorshipMessage,
  subscribeMentorshipMeta,
  markMentorshipRead,
  setMentorshipTyping,
  editMentorshipMessage,
  deleteMentorshipMessage,
} from "../../utils/mentorshipHelpers"

function formatMessageTime(ms) {
  if (!ms) return ""
  const d = new Date(ms)
  let hours = d.getHours()
  const minutes = d.getMinutes()
  const ampm = hours >= 12 ? "PM" : "AM"
  hours = hours % 12
  if (hours === 0) hours = 12
  const minutesStr = minutes < 10 ? `0${minutes}` : `${minutes}`
  return `${hours}:${minutesStr} ${ampm}`
}

// currentUser: { uid, name }
// otherUid / otherName: the other participant in this pairing
const MentorshipChat = ({ orgId, mentorshipId, currentUser, otherUid, otherName }) => {
  const [messages, setMessages] = useState([])
  const [meta, setMeta] = useState(null)
  const [text, setText] = useState("")
  const [sending, setSending] = useState(false)

  const [editingId, setEditingId] = useState(null)
  const [editText, setEditText] = useState("")
  const [savingEdit, setSavingEdit] = useState(false)

  const listRef = useRef(null)
  const typingTimeoutRef = useRef(null)
  const isTypingRef = useRef(false)

  useEffect(() => {
    if (!orgId || !mentorshipId) return
    return subscribeMentorshipMessages(orgId, mentorshipId, setMessages)
  }, [orgId, mentorshipId])

  useEffect(() => {
    if (!orgId || !mentorshipId) return
    return subscribeMentorshipMeta(orgId, mentorshipId, setMeta)
  }, [orgId, mentorshipId])

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "auto" })
  }, [messages.length])

  // Mark read shortly after messages settle, mirroring the debounce used
  // in the reference implementation — avoids a write on every snapshot tick.
  useEffect(() => {
    if (!orgId || !mentorshipId || !currentUser?.uid) return
    const t = setTimeout(() => {
      markMentorshipRead(orgId, mentorshipId, currentUser.uid).catch(() => {})
    }, 800)
    return () => clearTimeout(t)
  }, [orgId, mentorshipId, currentUser?.uid, messages.length])

  const stopTyping = () => {
    if (typingTimeoutRef.current) { clearTimeout(typingTimeoutRef.current); typingTimeoutRef.current = null }
    if (isTypingRef.current && orgId && mentorshipId && currentUser?.uid) {
      isTypingRef.current = false
      setMentorshipTyping(orgId, mentorshipId, currentUser.uid, false).catch(() => {})
    }
  }

  useEffect(() => () => stopTyping(), [orgId, mentorshipId]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleTextChange = (val) => {
    setText(val)
    if (!orgId || !mentorshipId || !currentUser?.uid) return
    if (val.trim()) {
      if (!isTypingRef.current) {
        isTypingRef.current = true
        setMentorshipTyping(orgId, mentorshipId, currentUser.uid, true).catch(() => {})
      }
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current)
      typingTimeoutRef.current = setTimeout(stopTyping, 3000)
    } else {
      stopTyping()
    }
  }

  const handleSend = async () => {
    const trimmed = text.trim()
    if (!trimmed || sending || !currentUser?.uid) return
    setSending(true)
    stopTyping()
    setText("")
    try {
      await sendMentorshipMessage(orgId, mentorshipId, {
        uid: currentUser.uid,
        name: currentUser.name || "",
        text: trimmed,
      })
    } finally {
      setSending(false)
    }
  }

  const handleEditStart = (m) => {
    setEditingId(m.id)
    setEditText(m.text || "")
  }

  const handleEditCancel = () => {
    setEditingId(null)
    setEditText("")
  }

  const handleEditSave = async () => {
    const trimmed = editText.trim()
    if (!trimmed || savingEdit) return
    setSavingEdit(true)
    try {
      await editMentorshipMessage(orgId, mentorshipId, editingId, trimmed)
      setEditingId(null)
      setEditText("")
    } finally {
      setSavingEdit(false)
    }
  }

  const handleDelete = async (m) => {
    if (!window.confirm("Delete this message? This can't be undone.")) return
    await deleteMentorshipMessage(orgId, mentorshipId, m.id)
  }

  const otherTyping = !!meta?.[`typing_${otherUid}`]
  const otherLastReadMs = meta?.[`lastRead_${otherUid}`]?.toMillis ? meta[`lastRead_${otherUid}`].toMillis() : null

  let lastMineId = null
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i]?.uid === currentUser?.uid) { lastMineId = messages[i].id; break }
  }

  return (
    <div className="bg-card border border-border rounded-xl flex flex-col" style={{ height: "480px" }}>
      <div className="px-5 py-3 border-b border-border">
        <p className="text-sm font-semibold text-gray-900">{otherName}</p>
        {otherTyping && <p className="text-xs text-accent">typing…</p>}
      </div>

      <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {messages?.length === 0 && (
          <p className="text-sm text-muted-foreground text-center pt-8">No messages yet. Say hello.</p>
        )}
        {messages?.map((m) => {
          const isMine = m?.uid === currentUser?.uid
          const createdAtMs = m?.createdAt?.toMillis ? m.createdAt.toMillis() : null
          const isLastMine = isMine && m?.id === lastMineId
          const isRead = isLastMine && otherLastReadMs != null && createdAtMs != null && createdAtMs <= otherLastReadMs
          const isEditingThis = editingId === m?.id

          return (
            <div key={m?.id} className={`group flex items-end gap-1.5 ${isMine ? "justify-end" : "justify-start"}`}>
              {isMine && !m?.deleted && !isEditingThis && (
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-smooth mb-1">
                  <button
                    onClick={() => handleEditStart(m)}
                    className="p-1 rounded hover:bg-muted"
                    title="Edit"
                  >
                    <Icon name="Pencil" size={12} className="text-muted-foreground" />
                  </button>
                  <button
                    onClick={() => handleDelete(m)}
                    className="p-1 rounded hover:bg-muted"
                    title="Delete"
                  >
                    <Icon name="Trash2" size={12} className="text-muted-foreground" />
                  </button>
                </div>
              )}

              <div className={`max-w-[75%] rounded-2xl px-3.5 py-2 ${isMine ? "bg-accent text-white rounded-br-sm" : "bg-muted text-gray-900 rounded-bl-sm"}`}>
                {isEditingThis ? (
                  <div className="min-w-[200px] space-y-1.5">
                    <textarea
                      rows={2}
                      value={editText}
                      onChange={(e) => setEditText(e?.target?.value)}
                      autoFocus
                      className="w-full resize-none rounded-md border border-white/30 bg-white/10 px-2 py-1.5 text-sm text-white placeholder-white/60 focus:outline-none"
                    />
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={handleEditCancel} className="text-xs text-white/80 hover:text-white">
                        Cancel
                      </button>
                      <button
                        onClick={handleEditSave}
                        disabled={!editText.trim() || savingEdit}
                        className="text-xs font-semibold text-white bg-white/20 hover:bg-white/30 rounded px-2 py-1 disabled:opacity-50"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                ) : m?.deleted ? (
                  <p className={`text-sm italic ${isMine ? "text-white/70" : "text-muted-foreground"}`}>
                    This message was deleted
                  </p>
                ) : (
                  <p className="text-sm whitespace-pre-wrap break-words">{m?.text}</p>
                )}

                {!isEditingThis && (
                  <div className={`text-[10px] mt-1 ${isMine ? "text-white/70" : "text-muted-foreground"}`}>
                    {formatMessageTime(createdAtMs)}
                    {m?.editedAt && !m?.deleted ? " · edited" : ""}
                    {isLastMine && isRead ? " · Read" : ""}
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>

      <div className="px-4 py-3 border-t border-border flex items-end gap-2">
        <textarea
          rows={1}
          value={text}
          onChange={(e) => handleTextChange(e?.target?.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault()
              handleSend()
            }
          }}
          placeholder="Message…"
          className="flex-1 resize-none rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring max-h-24"
        />
        <Button variant="default" size="icon" onClick={handleSend} disabled={!text.trim() || sending}>
          <Icon name="ArrowUp" size={16} />
        </Button>
      </div>
    </div>
  )
}

export default MentorshipChat